import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import axios from "axios";
import lottie, { type AnimationItem } from "lottie-web";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputDate } from "../../../components/form/FormInputDate";
import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

type Props = {
  data: any;
  onClose: () => void;
  openSuccessAlert?: (msg?: string) => void;
  openErrorAlert?: (msg?: string) => void;
  refreshPage?: () => void;
};

interface FormValues {
  name: string;
  startDate: string;
  endDate: string;
  mediaFile: File | null;
  status: string;
}

const styles = {
  sectionLabel: { fontSize: 16 },
  subLabel: { fontSize: 12 },
  alignCenter: { display: "flex", alignItems: "center" },
  imageContent: { width: 48, height: 86, marginLeft: "16px" },
  mediaPreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  lottiePreview: {
    width: "100%",
    height: "100%",
    overflow: "hidden" as const,
  },
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB — IMAGE/WEBP
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_JSON_SIZE = 1 * 1024 * 1024; // 1 MB — LOTTIE
const TARGET_ASPECT_RATIO = 9 / 16;
const ASPECT_RATIO_TOLERANCE = 0.02;

const formatDateInput = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";

export default function ModalEditSplashScreen({
  data,
  onClose,
  openSuccessAlert,
  openErrorAlert,
  refreshPage,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const lottieAnimationRef = useRef<AnimationItem | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isLottiePreview, setIsLottiePreview] = useState(false);
  const [loading, setLoading] = useState(false);


  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("splashScreen.edit.validation.nameRequired"))
      .test(
        "no-only-spaces",
        t("splashScreen.edit.validation.nameRequired"),
        (value) => !value || value.trim().length > 0,
      ),
    startDate: yup
      .string()
      .required(t("splashScreen.edit.validation.startDateRequired"))
      .test(
        "start-before-end",
        t("splashScreen.edit.validation.endDateGreaterThanStart"),
        (value, ctx) => {
          const { endDate } = ctx.parent as FormValues;
          if (!value || !endDate) return true;
          return value <= endDate;
        },
      ),
    endDate: yup
      .string()
      .required(t("splashScreen.edit.validation.endDateRequired"))
      .test(
        "end-after-start",
        t("splashScreen.edit.validation.endDateGreaterThanStart"),
        (value, ctx) => {
          const { startDate } = ctx.parent as FormValues;
          if (!startDate || !value) return true;
          return value > startDate;
        },
      ),
    mediaFile: yup.mixed<File>().nullable(),
    status: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: data?.title || data?.name || "",
      startDate: formatDateInput(data?.startDate),
      endDate: formatDateInput(data?.endDate),
      mediaFile: null,
      status: data?.status || "ACTIVE",
    },
  });

  const isValidVerticalRatio = (width: number, height: number) => {
    if (!width || !height) return false;
    const ratio = width / height;
    return Math.abs(ratio - TARGET_ASPECT_RATIO) <= ASPECT_RATIO_TOLERANCE;
  };

  const validateLottie = (animation: any): boolean => {
    if (!animation || typeof animation !== "object") return false;
    if (
      animation.v == null ||
      animation.fr == null ||
      animation.ip == null ||
      animation.op == null ||
      animation.layers == null
    )
      return false;
    if (animation.w == null || animation.h == null) return false;
    return isValidVerticalRatio(animation.w, animation.h);
  };

  const destroyLottiePreview = () => {
    lottieAnimationRef.current?.destroy();
    lottieAnimationRef.current = null;

    if (lottieContainerRef.current) {
      lottieContainerRef.current.innerHTML = "";
    }
  };

  const renderLottiePreview = (animationData: Record<string, unknown>) => {
    if (!lottieContainerRef.current) {
      throw new Error("Lottie container not found");
    }

    destroyLottiePreview();

    lottieAnimationRef.current = lottie.loadAnimation({
      container: lottieContainerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid meet",
      },
    });

    lottieAnimationRef.current.addEventListener("data_failed", () => {
      setIsLottiePreview(false);
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.edit.validation.mediaInvalid"),
      });
    });
  };

  const clearPreviews = () => {
    setMediaPreview(null);
    setVideoPreview(null);
    setIsLottiePreview(false);
    destroyLottiePreview();
  };

  useEffect(() => {
    const initializePreview = async () => {
      const mediaType = data?.mediaType;
      const imageUrl =
        data?.fileUrlMdpi || data?.fileUrlHdpi || data?.fileUrlXhdpi;
      const directUrl = data?.fileUrl || data?.media || null;

      clearPreviews();

      if (mediaType === "LOTTIE" && directUrl) {
        try {
          const response = await fetch(directUrl);
          const animationData = (await response.json()) as Record<
            string,
            unknown
          >;

          if (!validateLottie(animationData)) {
            throw new Error("Invalid Lottie data from API");
          }

          setIsLottiePreview(true);
          renderLottiePreview(animationData);
          return;
        } catch (error) {
          console.error("[SplashEdit] Failed to load Lottie preview:", error);
        }
      }

      if (mediaType === "VIDEO" && directUrl) {
        setVideoPreview(directUrl);
        return;
      }

      if (
        (mediaType === "IMAGE" || mediaType === "WEBP") &&
        (imageUrl || directUrl)
      ) {
        setMediaPreview(imageUrl || directUrl);
      }
    };

    void initializePreview();
  }, [data]);

  const validateImageDimensions = (file: File) =>
    new Promise<boolean>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        const valid = isValidVerticalRatio(image.width, image.height);
        URL.revokeObjectURL(objectUrl);
        resolve(valid);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };

      image.src = objectUrl;
    });

  const validateVideoDimensions = (file: File) =>
    new Promise<boolean>((resolve) => {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const valid = isValidVerticalRatio(video.videoWidth, video.videoHeight);
        URL.revokeObjectURL(objectUrl);
        resolve(valid);
      };

      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(false);
      };

      video.src = objectUrl;
    });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearPreviews();
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const isImage = ["jpg", "jpeg", "webp"].includes(extension);
    const isVideo = extension === "mp4";
    const isJson = extension === "json";

    if (!isImage && !isVideo && !isJson) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.edit.validation.mediaInvalid"),
      });
      clearPreviews();
      return;
    }

    const maxFileSize = isImage
      ? MAX_IMAGE_SIZE
      : isVideo
        ? MAX_VIDEO_SIZE
        : MAX_JSON_SIZE;

    if (file.size > maxFileSize) {
      setValue("mediaFile", null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setError("mediaFile", {
        type: "manual",
        message: t("splashScreen.edit.validation.mediaInvalid"),
      });
      clearPreviews();
      return;
    }

    if (isImage) {
      const validImageRatio = await validateImageDimensions(file);

      if (!validImageRatio) {
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.edit.validation.mediaInvalid"),
        });
        clearPreviews();
        return;
      }
    }

    if (isJson) {
      setMediaPreview(null);
      setVideoPreview(null);
      setIsLottiePreview(false);
      destroyLottiePreview();

      try {
        const text = await file.text();
        const lottieJson = JSON.parse(text) as Record<string, unknown>;

        if (!validateLottie(lottieJson)) {
          throw new Error("Invalid Lottie animation");
        }

        setValue("mediaFile", file, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });

        setIsLottiePreview(true);
        try {
          renderLottiePreview(lottieJson);
        } catch {
          setIsLottiePreview(false);
          setValue("mediaFile", null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
          setError("mediaFile", {
            type: "manual",
            message: t("splashScreen.edit.validation.mediaInvalid"),
          });
        }

        return;
      } catch {
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        clearPreviews();
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.edit.validation.mediaInvalid"),
        });
        return;
      }
    }

    if (isVideo) {
      const validVideoRatio = await validateVideoDimensions(file);

      if (!validVideoRatio) {
        setValue("mediaFile", null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        setError("mediaFile", {
          type: "manual",
          message: t("splashScreen.edit.validation.mediaInvalid"),
        });
        setVideoPreview(null);
        return;
      }
    }

    setValue("mediaFile", file, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    setIsLottiePreview(false);
    destroyLottiePreview();

    if (isImage) {
      setVideoPreview(null);
      const reader = new FileReader();
      reader.onload = () => setMediaPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else if (isVideo) {
      setMediaPreview(null);
      setVideoPreview(URL.createObjectURL(file));
    } else {
      setMediaPreview(null);
      setVideoPreview(null);
    }
  };

  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }

      destroyLottiePreview();
    };
  }, [videoPreview]);

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve((reader.result as string).split(",")[1] ?? "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  };

  const getExistingLottieFileBase64 = async (): Promise<string> => {
    const lottieUrl = data?.fileUrl || data?.media;

    if (!lottieUrl) {
      throw new Error("Missing current Lottie URL");
    }

    const response = await fetch(lottieUrl);
    if (!response.ok) {
      throw new Error("Failed to fetch current Lottie file");
    }

    const fileBuffer = await response.arrayBuffer();
    return arrayBufferToBase64(fileBuffer);
  };

  const getExistingMultipartFile = async (): Promise<File> => {
    const existingUrl =
      data?.fileUrl ||
      data?.media ||
      data?.fileUrlMdpi ||
      data?.fileUrlHdpi ||
      data?.fileUrlXhdpi;

    if (!existingUrl) {
      throw new Error("Missing current media URL");
    }

    const response = await fetch(existingUrl);
    if (!response.ok) {
      throw new Error("Failed to fetch current media file");
    }

    const fileBlob = await response.blob();
    const fileName =
      new URL(existingUrl).pathname.split("/").pop() || "media-file";

    return new File([fileBlob], fileName, {
      type: fileBlob.type || "application/octet-stream",
    });
  };

  const getMediaType = (file?: File | null) => {
    const extension = file?.name.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "jpg":
      case "jpeg":
        return "IMAGE";
      case "webp":
        return "WEBP";
      case "mp4":
        return "VIDEO";
      case "json":
        return "LOTTIE";
      default:
        return data?.mediaType ?? null;
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setLoading(true);
    try {
      const changeFile = values.mediaFile != null;
      const effectiveCountryCode = tokenHeaders.headers.countryCode;
      const splashHeaders = {
        authorization: tokenHeaders.headers.authorization,
        idToken: tokenHeaders.headers.idToken,
        countryCode: effectiveCountryCode,
      };
      const mediaType = getMediaType(values.mediaFile);
      const useLottieEndpoint = mediaType === "LOTTIE";
      const metadata = {
        countryCode: effectiveCountryCode,
        name: values.name.trim(),
        startDate: values.startDate,
        endDate: values.endDate,
        changeFile,
        active: values.status === "ACTIVE",
        mediaType,
      };

      if (useLottieEndpoint) {
        const fileBase64 =
          changeFile && values.mediaFile
            ? await toBase64(values.mediaFile)
            : await getExistingLottieFileBase64();

        const body: Record<string, unknown> = {
          ...metadata,
          fileBase64,
        };
        await axios.put(`${CONFIG.API_URL}splash/lottie/${data?.id}`, body, {
          headers: splashHeaders,
        });
      } else {
        const formData = new FormData();
        if (changeFile && values.mediaFile) {
          formData.append("file", values.mediaFile);
        } else {
          const existingFile = await getExistingMultipartFile();
          formData.append("file", existingFile);
        }
        formData.append("metadata", JSON.stringify(metadata));

        const headers = {
          ...splashHeaders,
          "Content-Type": "multipart/form-data",
        };

        await axios.put(
          `${CONFIG.API_URL}splash/contents/${data?.id}`,
          formData,
          {
            headers,
          },
        );
      }

      refreshPage?.();
      onClose();
      openSuccessAlert?.(t("splashScreen.alert.editSuccessMessage"));
    } catch (error: any) {
      const apiErrorMessage = error?.response?.data?.message;
      openErrorAlert?.(apiErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MyModal
      title={t("splashScreen.edit.title")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }} sx={styles.alignCenter}>
          <span style={styles.sectionLabel}>
            {t("splashScreen.edit.identification")}
          </span>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInputText
                id="name"
                type="text"
                label={t("splashScreen.edit.name")}
                sx={{ marginBottom: 0 }}
                slotProps={{ htmlInput: { maxLength: 100, minLength: 1 } }}
                error={errors.name}
                {...register("name")}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }} sx={styles.alignCenter}>
          <span style={styles.sectionLabel}>
            {t("splashScreen.edit.period")}
          </span>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FormInputDate
                  name="startDate"
                  control={control}
                  label={t("splashScreen.edit.startDate")}
                  minDate={dayjs()}
                  error={errors.startDate}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FormInputDate
                  name="endDate"
                  control={control}
                  label={t("splashScreen.edit.endDate")}
                  minDate={dayjs()}
                  error={errors.endDate}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <p style={{ ...styles.sectionLabel, margin: 0 }}>
            {t("splashScreen.edit.media")}
          </p>
          <p style={{ ...styles.subLabel, margin: 0 }}>
            {t("splashScreen.edit.mediaFormat")}
          </p>
          <p style={{ ...styles.subLabel, margin: 0 }}>
            {t("splashScreen.edit.mediaTypes")}
          </p>
          <p style={{ ...styles.subLabel, margin: 0 }}>
            {t("splashScreen.edit.mediaMaxSize")}
          </p>
        </Grid>

        <Grid
          size={{ xs: 12, md: 3 }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minHeight: 56,
          }}
        >
          <Button
            variant="contained"
            component="label"
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("splashScreen.edit.selectFile")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/webp,video/mp4,application/json,.json"
            hidden
            onChange={handleFileChange}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          <p style={styles.sectionLabel}>
            {t("splashScreen.edit.currentMedia")}
          </p>
          <div style={styles.imageContent}>
            {mediaPreview && (
              <img
                src={mediaPreview}
                alt={t("splashScreen.edit.currentMedia")}
                style={styles.mediaPreview}
              />
            )}
            {videoPreview && (
              <video
                src={videoPreview}
                autoPlay
                muted
                loop
                playsInline
                style={styles.mediaPreview}
              />
            )}
            <div
              ref={lottieContainerRef}
              style={{
                ...styles.lottiePreview,
                display: isLottiePreview ? "block" : "none",
              }}
            />
          </div>
        </Grid>

        {errors.mediaFile?.message && (
          <Grid size={{ xs: 12, md: 9 }} offset={{ md: 3 }}>
            <Box sx={{ color: "var(--red-100)", fontSize: "14px" }}>
              {String(errors.mediaFile.message)}
            </Box>
          </Grid>
        )}
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <span style={styles.sectionLabel}>
            {t("splashScreen.edit.publicationStatus")}
          </span>
          <p style={styles.subLabel}>
            {t("splashScreen.edit.publicationStatusHint")}
          </p>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value === "ACTIVE"}
                onChange={(e) =>
                  field.onChange(e.target.checked ? "ACTIVE" : "INACTIVE")
                }
              />
            )}
          />
        </Grid>
      </Grid>

      {HorizontalLine}
      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("splashScreen.edit.requiredFields")}
      </p>

      {loading || isSubmitting ? (
        <CircularProgress />
      ) : (
        <ActionButtons
          typeButton="submit"
          saveName={t("splashScreen.edit.saveButton")}
          disableSave={!isValid}
          enableLine={false}
        />
      )}
    </MyModal>
  );
}
